import { Controller } from "@hotwired/stimulus"

const IMAGE_URL = /\.(gif|jpe?g|tiff|png)(\?.*)?$/i
const MODAL_SIZES = ["modal-sm", "modal-md", "modal-lg"]

export default class extends Controller {
  connect() {
    this.element.dataset.modalControllerReady = "true"
    this.element.dispatchEvent(new CustomEvent("website:modal:connected"))
    if (this.element.classList.contains("open")) this.notifyWhenShown()
  }

  disconnect() {
    this.abortRemoteRequest()
    if (this.shownTimeout) clearTimeout(this.shownTimeout)
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.cancelPendingEscape()
    this.releaseTrigger()
    delete this.element.dataset.modalControllerReady
  }

  handleClick(event) {
    const dismiss = event.target.closest("[data-dismiss='modal']")
    if (dismiss?.closest(".modal") === this.element) {
      this.closeModal()
      return
    }

    if (event.target.classList.contains("modal-backdrop") && !this.element.classList.contains("static-backdrop")) {
      this.closeModal()
    }
  }

  resetBeforeCache() {
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = null
    this.clearAuthenticationReturnTo()
    this.suspend()
    this.clearRemoteContent()
  }

  request(event) {
    if (!event.params.url) return

    const method = event.params.method?.toUpperCase() || "GET"
    const headers = {}

    if (method !== "GET") {
      headers["X-CSRF-Token"] = document.querySelector("meta[name='csrf-token']")?.content || ""
    }

    fetch(event.params.url, { method, credentials: "same-origin", headers }).catch(() => {})
  }

  open(event) {
    this.openModal(event.detail?.trigger)
  }

  close() {
    this.closeModal()
  }

  closeOnEscape(event) {
    if (!this.element.classList.contains("open") || this.element.classList.contains("static-backdrop")) return

    this.cancelPendingEscape()

    let inputOccurred = false
    const inputTarget = event.target instanceof HTMLInputElement ? event.target : null
    const detectInput = () => { inputOccurred = true }

    inputTarget?.addEventListener("input", detectInput, { once: true })
    this.escapeInputTarget = inputTarget
    this.escapeInputHandler = detectInput
    this.escapeTimeout = setTimeout(() => {
      this.escapeTimeout = null
      this.removeEscapeInputListener()

      if (!inputOccurred && this.element.classList.contains("open")) this.closeModal()
    })
  }

  closeAfterSubmit() {
    this.hide()
    this.notify("hide")
    this.releaseTrigger(true)
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => {
      this.closeTimeout = null
      if (!this.element.isConnected) return

      this.abortRemoteRequest()
      this.clearRemoteContent()
    })
  }

  openModal(trigger = null) {
    this.abortRemoteRequest()
    document.querySelectorAll(".modal.open").forEach(openedModal => {
      if (openedModal !== this.element) openedModal.dispatchEvent(new CustomEvent("website:modal:suspend"))
    })

    if (trigger) {
      const url = trigger.getAttribute("aria-controls") === "modal" ? trigger.getAttribute("href") : null
      const configure = () => this.configureModal(trigger)

      if (url) this.showLoading(trigger.dataset.loadingText)
      if (url && this.element.classList.contains("open")) {
        this.loadContent(url, configure)
      } else {
        configure()
        this.loadContent(url)
      }
      this.trigger = trigger
      trigger.setAttribute("aria-expanded", "true")
    }

    delete this.element.dataset.modalReady
    this.element.classList.add("open")
    this.element.setAttribute("aria-hidden", "false")
    this.element.querySelector("[autofocus]")?.focus()
    this.notifyWhenShown()
  }

  closeModal() {
    if (this.element.id === "modal") this.abortRemoteRequest()
    this.hide()
    this.notify("hide")
    this.clearRemoteContent()
    this.releaseTrigger(true)
  }

  suspend() {
    this.abortRemoteRequest()
    this.hide()
    this.releaseTrigger()
  }

  hide() {
    this.cancelPendingEscape()
    if (this.shownTimeout) clearTimeout(this.shownTimeout)
    this.shownTimeout = null
    this.element.classList.remove("open")
    this.element.setAttribute("aria-hidden", "true")
    delete this.element.dataset.modalReady
  }

  configureModal(trigger) {
    Array.from(this.element.classList)
      .filter(className => className.startsWith("modal-"))
      .forEach(className => this.element.classList.remove(className))

    if (trigger.dataset.class) this.element.classList.add(`modal-${trigger.dataset.class}`)
    this.element.classList.remove(...MODAL_SIZES)
    this.element.classList.add(`modal-${trigger.dataset.size || "md"}`)
    this.element.classList.toggle("static-backdrop", trigger.dataset.staticBackdrop === "true")
    this.element.classList.toggle("mobile-full-size", trigger.dataset.mobileFullSize === "true")
    this.element.style.removeProperty("display")

    const title = trigger.dataset.title || trigger.getAttribute("title")
    const titleElement = this.element.querySelector(".modal-header .title")
    if (title && titleElement) titleElement.textContent = title === "&nbsp;" ? "\u00a0" : title

    this.configureAuthenticationReturnTo(trigger)
  }

  configureAuthenticationReturnTo(trigger) {
    const returnTo = trigger.dataset.returnTo || ""
    this.element.querySelectorAll("form[data-authentication-return-to-param]").forEach(form => {
      const paramName = form.dataset.authenticationReturnToParam
      let input = form.elements.namedItem(paramName)

      if (!returnTo) {
        input?.remove()
        return
      }

      if (!input) {
        input = document.createElement("input")
        input.type = "hidden"
        input.name = paramName
        form.append(input)
      }

      input.value = returnTo
    })
  }

  clearAuthenticationReturnTo() {
    this.element.querySelectorAll("form[data-authentication-return-to-param]").forEach(form => {
      form.elements.namedItem(form.dataset.authenticationReturnToParam)?.remove()
    })
  }

  showLoading(text) {
    if (!text) return

    const loading = document.createElement("div")
    loading.className = "modal-loading"
    loading.setAttribute("role", "status")
    loading.textContent = text
    this.element.querySelector(".modal-body")?.replaceChildren(loading)
  }

  loadContent(url, beforeReplace = null) {
    if (!url) return

    const body = this.element.querySelector(".modal-body")
    if (!body) return

    if (IMAGE_URL.test(url)) {
      const image = document.createElement("img")
      image.src = url
      image.className = "img-fluid mx-auto"
      image.addEventListener("load", () => this.resizeForImage(image), { once: true })
      beforeReplace?.()
      body.replaceChildren(image)
      return
    }

    this.abortRemoteRequest()
    this.remoteRequest = new AbortController()
    fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest"
      },
      signal: this.remoteRequest.signal
    })
      .then(response => {
        if (!response.ok) throw new Error(`Unable to load modal (${response.status})`)
        return response.text()
      })
      .then(html => {
        beforeReplace?.()
        this.replaceRemoteContent(body, html)
      })
      .catch(error => {
        if (error.name !== "AbortError") body.replaceChildren()
      })
  }

  replaceRemoteContent(body, html) {
    body.innerHTML = html
    body.querySelectorAll("script").forEach(script => {
      const replacement = document.createElement("script")
      Array.from(script.attributes).forEach(attribute => replacement.setAttribute(attribute.name, attribute.value))
      replacement.textContent = script.textContent
      script.replaceWith(replacement)
    })
    if (body.closest(".modal.open")) body.querySelector("[autofocus]")?.focus()
  }

  resizeForImage(image) {
    if (!this.element.classList.contains("modal-lg")) return

    const content = this.element.querySelector(".modal-content")
    const styles = getComputedStyle(content)
    const horizontalPadding = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight)
    content.style.maxWidth = `${image.naturalWidth + horizontalPadding}px`
  }

  notifyWhenShown() {
    if (this.shownTimeout) clearTimeout(this.shownTimeout)
    this.shownTimeout = setTimeout(() => {
      this.shownTimeout = null
      if (!this.element.classList.contains("open")) return

      this.element.dataset.modalReady = "true"
      this.notify("shown")
      this.element.querySelector("[autofocus]")?.focus()
    }, 300)
  }

  notify(name) {
    this.element.dispatchEvent(new CustomEvent(`website:modal:${name}`, { bubbles: true }))
  }

  clearRemoteContent() {
    if (this.element.id === "modal") this.element.querySelector(".modal-body")?.replaceChildren()
  }

  releaseTrigger(restoreFocus = false) {
    if (!this.trigger) return

    this.trigger.setAttribute("aria-expanded", "false")
    if (restoreFocus && this.trigger.isConnected) this.trigger.focus()
    this.trigger = null
  }

  abortRemoteRequest() {
    this.remoteRequest?.abort()
    this.remoteRequest = null
  }

  cancelPendingEscape() {
    if (this.escapeTimeout) clearTimeout(this.escapeTimeout)
    this.escapeTimeout = null
    this.removeEscapeInputListener()
  }

  removeEscapeInputListener() {
    this.escapeInputTarget?.removeEventListener("input", this.escapeInputHandler)
    this.escapeInputTarget = null
    this.escapeInputHandler = null
  }
}
